import { trigger } from '@angular/animations';
import { CdkPortal, DomPortalOutlet } from '@angular/cdk/portal';
import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Moment } from 'moment';
import { Subscription } from 'rxjs';

import { AnimationService } from '../core/ui/animation.service';
import { CalendarState } from './lib/calendar-state';
import {
  pgCalendarTransitionBackdrop,
  pgCalendarTransitionCalendar,
} from './pg-calendar.animations';

declare var moment: (_) => Moment;
let uid = 1;

@Component({
  selector: 'pg-calendar, grit-calendar',
  templateUrl: './pg-calendar.component.html',
  styleUrls: ['./pg-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [CalendarState],
  exportAs: 'pgCalendar, gritCalendar',
  animations: [
    trigger('transitionBackdrop', pgCalendarTransitionBackdrop),
    trigger('transitionCalendar', pgCalendarTransitionCalendar),
  ],
})
export class PgCalendarComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  minDate: Date;
  @Input()
  maxDate: Date;

  @Input()
  openMonth: Date;
  @Output()
  openMonthChange = new EventEmitter<Date>();

  @Input()
  format: string;

  @Output()
  backdropClicked = new EventEmitter<any>();

  calendarPortalOutlet: DomPortalOutlet;
  backdropPortalOutlet: DomPortalOutlet;

  @ViewChild('calendarPortal', { static: true })
  calendarPortal: CdkPortal;

  @ViewChild('backdropPortal', { static: true })
  backdropPortal: CdkPortal;

  dialogTitleId;

  get animationState() {
    return { value: this._open ? 'visible' : 'hidden' };
  }
  protected _open = false;

  @ViewChild('calendar', { read: ElementRef })
  _calendarChild: ElementRef;
  get calendarEl(): HTMLElement {
    return this._calendarChild.nativeElement;
  }

  @Output()
  selectedDatesChange = new EventEmitter<Date>();

  subs: Subscription[] = [];

  constructor(
    public calendarState: CalendarState,
    public animation: AnimationService,
    private _renderer: Renderer2,
    _appRef: ApplicationRef,
    _cmptFactResolver: ComponentFactoryResolver,
    _injector: Injector
  ) {
    this.dialogTitleId = 'calendar-title-' + uid++;
    this.calendarPortalOutlet = new DomPortalOutlet(
      document.body,
      _cmptFactResolver,
      _appRef,
      _injector
    );
    this.backdropPortalOutlet = new DomPortalOutlet(
      document.body,
      _cmptFactResolver,
      _appRef,
      _injector
    );
  }

  ngOnInit() {
    this.calendarState.selectedDate.subscribe(data => {
      if (data) {
        if (this.format) {
          data = moment(data).format(this.format);
        }
        this.selectedDatesChange.emit(data);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.minDate) {
      this.calendarState.minSelectableDate = changes.minDate.currentValue;
    }
    if (changes.maxDate) {
      this.calendarState.maxSelectableDate = changes.maxDate.currentValue;
    }
    if (changes.openMonth) {
      if (changes.openMonth.currentValue) {
        this.calendarState.openCalendarMonth.next(
          changes.openMonth.currentValue
        );
      }
    }
  }

  ngOnDestroy() {
    this.hide();

    if (this.backdropPortal.isAttached) {
      this.backdropPortal.detach();
    }
    if (this.calendarPortal.isAttached) {
      this.calendarPortal.detach();
    }

    this.subs.forEach(s => !s.closed && s.unsubscribe());
  }

  onCalendarTransitionDone(event) {
    if (event.fromState === 'hidden' && event.toState === 'visible') {
      this._renderer.setStyle(this.calendarEl, 'transform', null);
    }
  }

  show() {
    this._open = true;
    this.calendarPortalOutlet.attachTemplatePortal(this.calendarPortal);
    this.backdropPortalOutlet.attachTemplatePortal(this.backdropPortal);
  }

  hide() {
    this._open = false;

    this.backdropPortalOutlet.detach();
    this.calendarPortalOutlet.detach();
  }
}
